import axios from 'axios';

export interface AyreneRegistrationRequest {
    email: string;
    password: string;
    username: string;
}

export interface AyreneRegistrationResponse {
    success: boolean;
    message?: string;
}

export interface AyreneLoginRequest {
    email: string;
    password: string;
}

export interface AyreneLoginResponse {
    success: boolean;
    message?: string;
    token?: string;
    accessToken?: string;
    refreshToken?: string;
    data?: any;
}

export interface AyreneLogoutResponse {
    success: boolean;
    message?: string;
}

export interface AyreneProfileData {
    username: string;
    gender?: string;
    dateOfBirth?: string;
    address?: string;
}

export interface AyreneProfileUpdateRequest {
    username?: string;
    gender?: string;
    dateOfBirth?: string;
    address?: string;
}

export interface AyreneProfileResponse {
    success: boolean;
    message?: string;
    data?: any;
}

export interface AyreneRefreshTokenRequest {
    refreshToken: string;
}

export interface AyreneRefreshTokenResponse {
    success: boolean;
    message?: string;
    accessToken?: string;
    refreshToken?: string;
}

export interface AyreneUser {
    id: string;
    username: string;
    email: string;
    profilePicture?: string;
    gender?: string;
    dateOfBirth?: string;
    address?: string;
    bio?: string;
    interests?: string[];
    onlineStatus?: string;
    lastSeen?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface AyreneUserRaw {
    _id: string;
    username?: string;
    email: string;
    avatar?: string;
    onlineStatus?: string;
    lastSeen?: string;
}

export interface AyreneUsersApiResponse {
    users: AyreneUserRaw[];
}

export interface AyreneUsersResponse {
    success: boolean;
    message?: string;
    data?: AyreneUser[];
}

export interface AyreneUserByEmailRequest {
    email: string;
}

export interface AyreneUserByEmailResponse {
    success: boolean;
    message?: string;
    data?: AyreneUser;
    ayreneUserId?: string;
}

export interface AyreneDeleteProfileRequest {
    email: string;
}

export interface AyreneDeleteProfileResponse {
    success: boolean;
    message?: string;
}

const AYRENE_API_BASE_URL = 'https://api.ayrene.com/api/auth';
const AYRENE_API_USERS_URL = 'https://api.ayrene.com/api/users';

export const decodeJWT = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};

export const getTokenExpiration = (token: string): number | null => {
    const decoded = decodeJWT(token);
    return decoded?.exp ? decoded.exp * 1000 : null;
};

export const isTokenExpired = (token: string): boolean => {
    const expiration = getTokenExpiration(token);
    if (!expiration) return true;
    return Date.now() >= expiration;
};

export const getMinutesUntilExpiration = (token: string): number => {
    const expiration = getTokenExpiration(token);
    if (!expiration) return 0;
    const timeLeft = expiration - Date.now();
    return Math.floor(timeLeft / 60000);
};

export const getCurrentAyreneUserId = (): string | null => {
    try {
        const token = localStorage.getItem('ayreneAuthToken');
        if (!token) {
            console.warn('⚠️ No Ayrene token found in localStorage');
            return null;
        }

        const decoded = decodeJWT(token);
        if (!decoded || !decoded.userId) {
            console.warn('⚠️ Could not extract userId from token');
            return null;
        }

        return decoded.userId;
    } catch (error) {
        console.error('❌ Error getting current Ayrene user ID:', error);
        return null;
    }
};

export const ayreneService = {
    register: async (data: AyreneRegistrationRequest): Promise<AyreneRegistrationResponse> => {
        try {
            const response = await axios.post(
                `${AYRENE_API_BASE_URL}/register`,
                data,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Ayrene registration successful:', response.data);
            return { success: true, message: 'Ayrene registration successful' };
        } catch (error: any) {
            console.error('Ayrene registration failed:', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || 'Ayrene registration failed'
            };
        }
    },

    login: async (data: AyreneLoginRequest): Promise<AyreneLoginResponse> => {
        console.log('🔄 Attempting Ayrene login for email:', data.email);

        try {
            const response = await axios.post(
                `${AYRENE_API_BASE_URL}/login`,
                data,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('✅ Ayrene login successful!');
            console.log('📦 Ayrene response data:', response.data);

            const accessToken = response.data.accessToken;
            const refreshToken = response.data.refreshToken;

            if (accessToken) {
                console.log('🔑 Access token retrieved:', accessToken.substring(0, 20) + '...');
            }

            if (refreshToken) {
                console.log('🔄 Refresh token retrieved:', refreshToken.substring(0, 20) + '...');
            }

            return {
                success: true,
                message: 'Ayrene login successful',
                token: accessToken,
                accessToken: accessToken,
                refreshToken: refreshToken,
                data: response.data
            };
        } catch (error: any) {
            console.error('❌ Ayrene login failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.message || 'Ayrene login failed'
            };
        }
    },

    refreshToken: async (): Promise<AyreneRefreshTokenResponse> => {
        console.log('🔄 Attempting to refresh Ayrene token...');

        try {
            const accessToken = localStorage.getItem('ayreneAuthToken');
            const refreshToken = localStorage.getItem('ayreneRefreshToken');

            if (!refreshToken) {
                console.warn('⚠️ No refresh token found in localStorage');
                return {
                    success: false,
                    message: 'No refresh token found'
                };
            }

            if (!accessToken) {
                console.warn('⚠️ No access token found in localStorage');
                return {
                    success: false,
                    message: 'No access token found'
                };
            }

            const response = await axios.post(
                `${AYRENE_API_BASE_URL}/refresh`,
                { refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            console.log('✅ Token refresh successful!');

            const newAccessToken = response.data.accessToken;
            const newRefreshToken = response.data.refreshToken;

            if (newAccessToken) {
                localStorage.setItem('ayreneAuthToken', newAccessToken);
                const expiration = getTokenExpiration(newAccessToken);
                if (expiration) {
                    localStorage.setItem('ayreneTokenExpiration', expiration.toString());
                }
                console.log('💾 New access token stored');
            }

            if (newRefreshToken) {
                localStorage.setItem('ayreneRefreshToken', newRefreshToken);
                console.log('💾 New refresh token stored');
            }

            return {
                success: true,
                message: 'Token refreshed successfully',
                accessToken: newAccessToken,
                refreshToken: newRefreshToken
            };
        } catch (error: any) {
            console.error('❌ Token refresh failed:', error.response?.data || error.message);

            if (error.response?.status === 401) {
                console.warn('🚪 Refresh token expired, clearing storage');
                localStorage.removeItem('ayreneAuthToken');
                localStorage.removeItem('ayreneRefreshToken');
                localStorage.removeItem('ayreneTokenExpiration');
                localStorage.removeItem('ayreneLoginStatus');
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Token refresh failed'
            };
        }
    },

    logout: async (): Promise<AyreneLogoutResponse> => {
        console.log('🔄 Attempting Ayrene logout...');

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            const response = await axios.post(
                `${AYRENE_API_BASE_URL}/logout`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('✅ Ayrene logout successful!');
            console.log('📦 Ayrene logout response:', response.data);

            localStorage.removeItem('ayreneAuthToken');
            localStorage.removeItem('ayreneRefreshToken');
            localStorage.removeItem('ayreneTokenExpiration');

            return {
                success: true,
                message: 'Ayrene logout successful'
            };
        } catch (error: any) {
            console.error('❌ Ayrene logout failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.message || 'Ayrene logout failed'
            };
        }
    },

    getUserProfile: async (): Promise<AyreneProfileResponse> => {
        console.log('🔄 Fetching Ayrene user profile...');

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            const response = await axios.get(
                `${AYRENE_API_BASE_URL}/me`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('✅ Ayrene profile fetched successfully!');
            console.log('📦 Ayrene profile data:', response.data);

            return {
                success: true,
                message: 'Profile fetched successfully',
                data: response.data
            };
        } catch (error: any) {
            console.error('❌ Ayrene profile fetch failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch profile'
            };
        }
    },

    updateUserProfile: async (profileData: AyreneProfileUpdateRequest): Promise<AyreneProfileResponse> => {
        console.log('🔄 Updating Ayrene user profile...');
        console.log('📤 Exact payload being sent to Ayrene:', JSON.stringify(profileData, null, 2));

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            console.log('🔑 Using Ayrene auth token:', token.substring(0, 20) + '...');
            console.log('🌐 API endpoint:', `${AYRENE_API_USERS_URL}/profile`);

            const response = await axios.put(
                `${AYRENE_API_USERS_URL}/profile`,
                profileData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('✅ Ayrene profile updated successfully!');
            console.log('📦 Ayrene response:', response.data);

            return {
                success: true,
                message: 'Profile updated successfully',
                data: response.data
            };
        } catch (error: any) {
            console.error('❌ Ayrene profile update failed!');
            console.error('📤 Payload that was sent:', JSON.stringify(profileData, null, 2));
            console.error('🔍 Full error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
            });

            if (error.response?.data?.errors) {
                console.error('⚠️ Validation errors from Ayrene:', error.response.data.errors);
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update profile'
            };
        }
    },

    getAllUsers: async (): Promise<AyreneUsersResponse> => {
        console.log('🔄 Fetching all Ayrene users...');

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            const response = await axios.get<AyreneUsersApiResponse>(
                AYRENE_API_USERS_URL,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('✅ Ayrene users fetched successfully!');
            console.log('📦 Number of users:', response.data?.users?.length || 0);

            const rawUsers = response.data?.users || [];
            const transformedUsers: AyreneUser[] = rawUsers.map((user: AyreneUserRaw) => ({
                id: user._id,
                username: user.username || user.email,
                email: user.email,
                profilePicture: user.avatar || '',
                onlineStatus: user.onlineStatus,
                lastSeen: user.lastSeen
            }));

            return {
                success: true,
                message: 'Users fetched successfully',
                data: transformedUsers
            };
        } catch (error: any) {
            console.error('❌ Ayrene users fetch failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch users'
            };
        }
    },

    getUserByEmail: async (email: string): Promise<AyreneUserByEmailResponse> => {
        console.log('🔄 Fetching Ayrene user by email:', email);

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            const response = await axios.post(
                `${AYRENE_API_USERS_URL}/profile/by-email`,
                { email: email },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('✅ Ayrene user fetched successfully by email!');
            console.log('📦 User data:', response.data);

            const rawUser = response.data.profile || response.data;
            const transformedUser: AyreneUser = {
                id: rawUser._id,
                username: rawUser.username || rawUser.email,
                email: rawUser.email,
                profilePicture: rawUser.avatar || '',
                gender: rawUser.gender,
                dateOfBirth: rawUser.dateOfBirth,
                address: rawUser.address,
                bio: rawUser.bio,
                interests: rawUser.interests,
                onlineStatus: rawUser.onlineStatus,
                lastSeen: rawUser.lastSeen,
                createdAt: rawUser.createdAt,
                updatedAt: rawUser.updatedAt
            };

            return {
                success: true,
                message: 'User fetched successfully',
                data: transformedUser,
                ayreneUserId: rawUser._id
            };
        } catch (error: any) {
            console.error('❌ Ayrene user fetch by email failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            if (error.response?.status === 404) {
                return {
                    success: false,
                    message: 'User not found on Ayrene'
                };
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch user by email'
            };
        }
    },

    deleteProfileByEmail: async (email: string): Promise<AyreneDeleteProfileResponse> => {
        console.log('🔄 Attempting to delete Ayrene profile for email:', email);

        try {
            const token = localStorage.getItem('ayreneAuthToken');

            if (!token) {
                console.warn('⚠️ No Ayrene token found in localStorage');
                return {
                    success: false,
                    message: 'No authentication token found'
                };
            }

            const response = await axios.delete(
                `${AYRENE_API_BASE_URL}/delete-by-email`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    data: { email: email }
                }
            );

            console.log('✅ Ayrene profile deleted successfully!');
            console.log('📦 Delete response:', response.data);

            return {
                success: true,
                message: response.data.message || 'User deleted successfully'
            };
        } catch (error: any) {
            console.error('❌ Ayrene profile deletion failed:', error.response?.data || error.message);
            console.error('🔍 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete Ayrene profile'
            };
        }
    }
};
